import { EmeraldError, EmeraldErrorModal } from 'src/common/api/emerald-error';
import { AbortedError, ProhibitActionError } from 'src/common/api/extended-emerald-error';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { isNumber } from 'src/common/helpers/number-helper';
import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';
import { IMessageModal } from 'src/common/interfaces/dtos/IMessageModal';
import { ILineItemAddData } from 'src/items-list/business-logic/line-item-add';
import { lookupOnlineItemBehavior } from 'src/online-items/business-logic/get-online-item-behavior';
import { SellingBehaviorType } from 'src/online-items/enumerations/OnlineItemAuthorizationTrigger';
import { popEmeraldModal } from '../../common/emerald-modal-manager/emerald-modal-manager';
import { PriceEntryType } from '../../common/enumerations/price-entry-type';
import { IItemDetails } from '../../common/interfaces/dtos/IItemDetails';
import { popupAsyncMessage } from '../../messages/business-logic/popup-message';
import { showPriceEntryDialog } from '../components/price-entry-modal';
import { showHandleOnlineItemPriceEntry } from '../components/price-entry-online-item';
import { sellPriceEntryItem } from './sell-price-entry-item';

let modalId;
const SHOW_PRICE_MODAL = 'show-online-item-price-entry-modal';
const PRICE_VERIFY_MODAL = 'PRICE_VERIFY_MODAL';

export const handlePriceEntryExceptions = async (
  priceEntryType: PriceEntryType,
  lineItemData: ILineItemAddData,
  itemDetails: IItemDetails,
): Promise<void> => {
  const isPriceVerify = priceEntryType === PriceEntryType.PriceVerify;
  const span = createSpan('handle-error-user-input');
  const onlineItemBehavior: boolean | undefined = await lookupOnlineItemBehavior(
    lineItemData.itemLookup.linkedItemsGroup?.groupCategory,
  )
    .then(result => {
      return result.behaviorType === SellingBehaviorType.OnlineItemType ? true : false;
    })
    .catch(() => undefined);
  const initialPrice = isPriceVerify ? itemDetails.price.toString() : '';
  const title = isPriceVerify ? 'Verify Price' : `Price Required`;

  return new Promise<void>((resolve, reject) => {
    const onApprove = (newPrice: string): void => {
      endSpan(span);
      popEmeraldModal(SHOW_PRICE_MODAL);
      let modalMessage: IMessageModal;

      if (!isNumber(newPrice)) {
        return reject(new EmeraldError('RequiredPrice_InvalidInput'));
      } else if (onlineItemBehavior && newPrice === '0') {
        return reject(
          new EmeraldErrorModal({
            message: 'POSKeyPad_IllegalPrice',
            type: EmeraldDialogTypes.Warning,
            subMessage: 'POSKeyPadControl_PriceGreaterThanZero',
          }),
        );
      } else {
        sellPriceEntryItem(lineItemData, Number(newPrice), isPriceVerify)
          .then(resolve)
          .catch(async err => {
            if (err instanceof AbortedError || err instanceof ProhibitActionError) {
              reject(err);
            } else if (err instanceof EmeraldErrorModal) {
              modalMessage = err.errorModal;
            } else {
              modalMessage = {
                message: err.code,
                subMessage: err.subMessage,
                type: EmeraldDialogTypes.Warning,
              };
            }
            await popupAsyncMessage(modalMessage);
            reject(new AbortedError());
          });
      }
    };
    const onClose = () => {
      endSpan(span);
      reject(new AbortedError());
    };

    if (onlineItemBehavior) {
      modalId = SHOW_PRICE_MODAL;
      showHandleOnlineItemPriceEntry(
        {
          onClose,
          onApprove,
          lineItemData,
        },
        modalId,
      );
    } else {
      modalId = PRICE_VERIFY_MODAL;
      showPriceEntryDialog(
        {
          onClose,
          onApprove,
          itemDetails,
          initialPrice,
          title,
          priceEntryType,
        },
        modalId,
      );
    }
  }).finally(() => popEmeraldModal(modalId));
};
